import { Box, type BoxProps, Button, Menu } from '@mantine/core';
import type { ColumnOrderState, Table } from '@tanstack/react-table';
import { SettingsIcon } from 'icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type UseUserPreferenceResult,
  useBoardPreferenceAdmin,
  usePreferenceAdminActions,
} from 'shared/lib/preferences';
import type { TablePreferences } from '../../model';
import { ColumnList } from './ColumnList';

export type ColumnSettingsProps = BoxProps & {
  table: Table<any>;
  setColumnOrder: (newOrder: ColumnOrderState) => void;
  /** Logical preference key suffix (`tables.${preferenceKey}`). */
  preferenceKey: string;
  boardId?: number;
  preference: UseUserPreferenceResult<TablePreferences> | null;
  showAdminActions: boolean;
};

export const ColumnSettings: React.FC<ColumnSettingsProps> = ({
  table,
  setColumnOrder,
  preferenceKey,
  boardId,
  preference,
  showAdminActions,
  ...props
}) => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation('common');

  const admin = useBoardPreferenceAdmin<TablePreferences>(
    boardId,
    `tables.${preferenceKey}`,
  );

  const hasBoardDefault = preference?.boardLayer !== null;
  const hasUserOverride = preference?.userLayer !== null;
  const showResetUser = Boolean(preference) && hasUserOverride;
  const showAdminSection =
    showAdminActions && Boolean(boardId) && Boolean(preference);
  const showAnyPreferenceItem = showResetUser || showAdminSection;

  // Hook is called unconditionally (Rules of Hooks). When `preference` is
  // null we still need a stable currentValue / admin handle, so we fall
  // back to safe defaults — the resulting menuItems / confirmModals are
  // simply never rendered.
  const fallbackValue = {} as TablePreferences;
  const adminActions = usePreferenceAdminActions<TablePreferences>({
    currentValue: preference?.value ?? fallbackValue,
    hasBoardDefault,
    admin,
  });

  return (
    <Box {...props}>
      <Menu
        withinPortal
        position="bottom-end"
        shadow="md"
        width={300}
        opened={opened}
        onChange={setOpened}
        closeOnClickOutside
        closeOnEscape
      >
        <Menu.Target>
          <Button
            px={8}
            variant="default"
            aria-label="Column settings"
            sx={theme => ({
              background: theme.colors.violet[1],
            })}
          >
            <SettingsIcon size={24} color="gray" />
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          <Box p="xs">
            <ColumnList table={table} setColumnOrder={setColumnOrder} />
          </Box>
          {showAnyPreferenceItem && (
            <>
              <Menu.Divider />
              {showAdminSection && preference && adminActions.menuItems}
              {showResetUser && (
                <Menu.Item
                  data-testid="preferences-reset-to-default"
                  onClick={() => preference?.reset()}
                >
                  {t('preferences.resetToDefault')}
                </Menu.Item>
              )}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {/*
        Confirm modals live OUTSIDE the Menu so their owning state is not
        unmounted when Mantine closes the dropdown after the user clicks
        a Menu.Item. Without this lift, the modal mounts and immediately
        unmounts together with Menu.Dropdown contents (visible as a brief
        flash).
      */}
      {showAdminSection && adminActions.confirmModals}
    </Box>
  );
};
